/**
 * MerchantMarketplaceCarousel.jsx
 * ESDRAS MERCHANT MARKETPLACE — Homepage Product Carousel
 * Displays: approved, live products in a scrollable horizontal strip
 * Placed: below "Are you a Stylist?" section on Home.jsx
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD       = '#B8860B';
const DEEP_BLACK = '#050a0f';
const CARD_BG    = '#0a0f14';
const SAFE_IMG   = 'https://placehold.co/400x400/050a0f/B8860B?text=Esdras+Market';

const CATEGORY_LABELS = {
  hair_care:  { label: 'HAIR CARE',  icon: '💆', color: GOLD },
  skin_care:  { label: 'SKIN CARE',  icon: '✨', color: '#a8dadc' },
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function MerchantMarketplaceCarousel() {
  const navigate    = useNavigate();
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  // ── Real-time listener for approved/live products ────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, 'merchant_products'),
      where('status', '==', 'approved'),
      where('liveOnHomepage', '==', true),
      orderBy('approvedAt', 'desc'),
      limit(30),
    );

    const unsub = onSnapshot(q,
      (snap) => {
        setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.warn('Marketplace carousel fetch limited:', err.code);
        setProducts([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  if (loading) return <LoadingStrip />;

  return (
    <section style={styles.section}>
      {/* ── Section Header ── */}
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.sectionTitle}>🛍️ ESDRAS MARKETPLACE</h2>
          <p style={styles.sectionSub}>Premium hair care & skin care, curated & verified</p>
        </div>
        {products.length > 0 && (
          <button onClick={() => navigate('/marketplace')} style={styles.viewAllBtn}>
            VIEW ALL →
          </button>
        )}
      </div>

      {/* ── Empty State ── */}
      {products.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🌿</div>
          <p style={{ color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '0 0 6px' }}>
            MARKETPLACE LAUNCHING SOON
          </p>
          <p style={{ color: '#444', fontSize: '0.82rem', maxWidth: '320px', lineHeight: '1.6' }}>
            Our curated selection of premium hair and skin care products is being prepared.
            Check back shortly — or be the first to sell on Esdras.
          </p>
        </div>
      )}

      {/* ── Product Cards Strip ── */}
      {products.length > 0 && (
        <div style={styles.strip} className="merchant-carousel-strip hide-scrollbar">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onClick={() => navigate(`/marketplace/product/${product.id}`)} />
          ))}
          {/* Faded right edge hint */}
          <div style={styles.fadeRight} />
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .merchant-card {
          transition: transform 0.3s cubic-bezier(0.175,0.885,0.32,1.275),
                      box-shadow 0.3s ease;
          cursor: pointer;
        }
        .merchant-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.6), 0 0 20px rgba(184,134,11,0.15) !important;
        }
        .merchant-card:hover .card-img { transform: scale(1.06); }
      `}</style>
    </section>
  );
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
function ProductCard({ product, onClick }) {
  const [imgError, setImgError] = useState(false);
  const catInfo = CATEGORY_LABELS[product.category] || { label: product.category?.toUpperCase() || 'PRODUCT', icon: '🛒', color: GOLD };
  const imageUrl = (!imgError && product.imageUrls?.[0]) ? product.imageUrls[0] : SAFE_IMG;

  return (
    <div className="merchant-card" style={styles.card} onClick={onClick}>
      {/* Category Badge */}
      <div style={{ ...styles.catBadge, color: catInfo.color, borderColor: catInfo.color + '44' }}>
        {catInfo.icon} {catInfo.label}
      </div>

      {/* Product Image */}
      <div style={styles.imgWrapper}>
        <img
          src={imageUrl} alt={product.name}
          style={styles.cardImg} className="card-img"
          onError={() => setImgError(true)}
        />
        <div style={styles.imgOverlay} />
      </div>

      {/* Info */}
      <div style={styles.cardInfo}>
        <p style={styles.merchantName}>{product.merchantName || 'Esdras Merchant'}</p>
        <p style={styles.productName}>{product.name}</p>
        <div style={styles.priceRow}>
          <span style={styles.price}>₦{Number(product.displayPrice || 0).toLocaleString()}</span>
          {product.qty === 0 && (
            <span style={styles.outOfStock}>OUT OF STOCK</span>
          )}
        </div>
        {product.averageRating > 0 && (
          <div style={styles.ratingRow}>
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} style={{ color: i < Math.round(product.averageRating) ? GOLD : '#2a2a2a', fontSize: '0.65rem' }}>★</span>
            ))}
            <span style={{ color: '#555', fontSize: '0.68rem', marginLeft: '4px' }}>
              ({product.reviewCount || 0})
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LOADING STRIP ────────────────────────────────────────────────────────────
function LoadingStrip() {
  return (
    <section style={styles.section}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.sectionTitle}>🛍️ ESDRAS MARKETPLACE</h2>
          <p style={styles.sectionSub}>Premium hair care & skin care, curated & verified</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '16px', overflowX: 'hidden', padding: '4px 40px' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{
            minWidth: '220px', height: '310px', borderRadius: '16px',
            background: 'linear-gradient(90deg, #0a0f14 25%, #111820 50%, #0a0f14 75%)',
            backgroundSize: '200% 100%',
            animation: `shimmer 1.5s ease-in-out ${i * 0.15}s infinite`,
          }} />
        ))}
      </div>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const styles = {
  section: {
    padding: '70px 0 60px', background: '#020609',
    width: '100%', boxSizing: 'border-box', position: 'relative',
    borderTop: '1px solid #0a0f14',
  },
  headerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    padding: '0 40px', marginBottom: '28px', maxWidth: '1400px', margin: '0 auto 28px',
  },
  sectionTitle: {
    color: 'white', fontWeight: '900', margin: '0 0 5px',
    letterSpacing: '3px', fontSize: 'clamp(1rem, 2.5vw, 1.4rem)',
  },
  sectionSub: {
    color: '#444', fontSize: '0.8rem', margin: 0, letterSpacing: '1px',
  },
  viewAllBtn: {
    background: 'transparent', color: GOLD, border: `1px solid ${GOLD}55`,
    padding: '8px 20px', borderRadius: '50px', cursor: 'pointer',
    fontSize: '0.72rem', letterSpacing: '1px', fontWeight: '700',
    transition: 'all 0.2s',
  },
  strip: {
    display: 'flex', overflowX: 'auto', gap: '16px',
    padding: '10px 40px 20px', position: 'relative',
  },
  fadeRight: {
    position: 'absolute', right: 0, top: 0, bottom: 0,
    width: '80px', background: 'linear-gradient(to right, transparent, #020609)',
    pointerEvents: 'none',
  },
  emptyState: {
    textAlign: 'center', padding: '50px 20px',
    border: `1px dashed ${GOLD}22`, borderRadius: '20px',
    margin: '0 40px', background: `rgba(184,134,11,0.02)`,
  },
  card: {
    minWidth: '220px', maxWidth: '220px', background: CARD_BG,
    borderRadius: '16px', overflow: 'hidden', position: 'relative',
    border: '1px solid #1a2631', flexShrink: 0,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  catBadge: {
    position: 'absolute', top: '10px', left: '10px', zIndex: 3,
    fontSize: '0.6rem', fontWeight: '900', letterSpacing: '0.5px',
    background: 'rgba(5,10,15,0.85)', border: '1px solid',
    padding: '3px 8px', borderRadius: '20px', backdropFilter: 'blur(8px)',
  },
  imgWrapper: {
    height: '220px', overflow: 'hidden', position: 'relative', background: '#060b10',
  },
  cardImg: {
    width: '100%', height: '100%', objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  imgOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0, height: '60px',
    background: 'linear-gradient(transparent, #0a0f14)',
  },
  cardInfo: { padding: '12px 14px 14px' },
  merchantName: {
    color: '#555', fontSize: '0.65rem', margin: '0 0 4px',
    letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  productName: {
    color: 'white', fontWeight: '700', fontSize: '0.88rem',
    margin: '0 0 8px', lineHeight: '1.3',
    display: '-webkit-box', WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  priceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  price: { color: GOLD, fontWeight: '900', fontSize: '1rem' },
  outOfStock: {
    background: '#1a0a0a', color: '#ff4d4d', fontSize: '0.58rem',
    padding: '2px 7px', borderRadius: '4px', fontWeight: '700', letterSpacing: '0.5px',
  },
  ratingRow: { display: 'flex', alignItems: 'center', marginTop: '6px' },
};
